import { Children, forwardRef, type ReactNode, type ButtonHTMLAttributes } from 'react'
import styles from './DiagButton.module.css'

type DiagButtonVariant = 'default' | 'primary' | 'danger' | 'link' | 'text' | 'ghost' | 'link-chip'
type DiagButtonSize = 'small' | 'default' | 'large'

interface DiagButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: DiagButtonVariant
  size?: DiagButtonSize
  icon?: ReactNode
  badge?: number | string
  loading?: boolean
  /** Full-width button (antd `block`). */
  block?: boolean
}

/**
 * Replaces antd <Button>. variant="link" is underline-less text that
 * reads as a link; variant="text" is a borderless button matching antd
 * <Button type="text">. `block` stretches to 100% width.
 */
const DiagButton = forwardRef<HTMLButtonElement, DiagButtonProps>(function DiagButton({
  variant = 'default',
  size = 'default',
  icon,
  badge,
  loading,
  block,
  children,
  className,
  disabled,
  type,
  ...rest
}, ref) {
  const cls = [
    styles.btn,
    styles[variant],
    size !== 'default' && styles[size],
    block && styles.block,
    loading && styles.loading,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      ref={ref}
      className={cls}
      disabled={disabled || loading}
      type={type ?? 'button'}
      data-variant={variant}
      {...rest}
    >
      {loading && <span className={styles.spinner} aria-hidden="true" />}
      {!loading && icon}
      {children}
      {variant === 'link-chip' && (
        <span aria-hidden="true" className={styles['link-chip-arrow']}> →</span>
      )}
      {badge !== undefined && <span className={styles.badge}>{badge}</span>}
    </button>
  )
})

export default DiagButton

interface DiagButtonGroupProps {
  children: ReactNode
  size?: DiagButtonSize
  className?: string
}

/**
 * Pill-joins sibling DiagButtons horizontally, matching antd Button.Group.
 * The `size` prop is informational (children own their size) but is
 * forwarded as a data attribute for call-site styling if needed.
 */
export function DiagButtonGroup({ children, size, className }: DiagButtonGroupProps) {
  const cls = [styles.group, className].filter(Boolean).join(' ')
  return (
    <div className={cls} role="group" data-size={size}>
      {Children.toArray(children)}
    </div>
  )
}
